import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalSizes,
  ButtonVariants,
  Stepper,
  Table,
  Alert,
  AlertVariants,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader, resolveRowUnitType } from '@/hooks';
import { SummaryCard, DiscrepancyModal } from '@/components/common';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildTableLabels } from '@/config/tableLabels';
import { EMPTY_LIST } from '@/lib/utils';
import { buildAllocationDetailFields } from '../constants/allocationDetailFields';
import { buildUnitHeaderOptions } from '../constants/allocationUnits';
import {
  buildAllocationLinkStep1Columns,
  buildAllocationLinkStep2Columns,
} from '../constants/allocationLinkColumns';
import {
  buildSustainabilityMismatches,
  buildLinkShipmentDiscrepancySections,
} from '../constants/allocationLinkDiscrepancy';
import styles from './LinkShipmentsForAllocation.module.scss';

const SELECT_STEP = 0;
const ASSIGN_STEP = 1;
const getRowId = (row) => row.id;

/**
 * Props:
 *   open        whether the wizard is shown
 *   onClose     ✕ / Cancel / Escape
 *   allocation  the Pending shipment allocation the shipments attach to
 *   shipments   linkable outgoing-shipment row view models
 *   loading     whether the candidate list is still fetching
 *   isSaving    disables Save while the mutation is in flight
 *   onSearchChange (term) => void — the candidate list filters server-side
 *   onSave      (entries) => void — `{ rowId, quantity, unitSymbol }` per assigned row
 */
export const LinkShipmentsForAllocation = ({
  open,
  onClose,
  allocation,
  shipments = EMPTY_LIST,
  loading = false,
  isSaving = false,
  onSearchChange,
  onSave,
}) => {
  const t = useTranslations();
  const [step, setStep] = useState(SELECT_STEP);
  const [selectedIds, setSelectedIds] = useState(EMPTY_LIST);
  const [assignments, setAssignments] = useState({});
  // The Table's key — it owns its search, selection and page, so only a remount clears them.
  const [session, setSession] = useState(0);
  const [error, setError] = useState(null);
  const [discrepancyRow, setDiscrepancyRow] = useState(null);

  const step2Rows = useMemo(
    () => shipments.filter((row) => selectedIds.includes(row.id)),
    [shipments, selectedIds],
  );

  const unitRows = step === SELECT_STEP ? shipments : step2Rows;
  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    unitRows,
    buildUnitHeaderOptions,
    t,
  );

  useEffect(() => {
    if (!open) return;
    setStep(SELECT_STEP);
    setSelectedIds(EMPTY_LIST);
    setAssignments({});
    setError(null);
    setSession((previous) => previous + 1);
    onSearchChange?.('');
    // onSearchChange is the parent's setter; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handleSelectionChange = useCallback((rows, ids) => setSelectedIds(ids), []);
  const handleSearchChange = useCallback((value) => onSearchChange?.(value), [onSearchChange]);

  // TODO: no route fills `sustainabilityMismatches` on a candidate row yet.
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  const handleAssign = useCallback((rowId, quantity) => {
    setAssignments((prev) => ({ ...prev, [rowId]: quantity }));
  }, []);

  const handleRemove = useCallback((rowId) => {
    setSelectedIds((prev) => prev.filter((id) => id !== rowId));
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  // Seeding a blank assignment per row is also what puts a shipment on step 2.
  const goNext = useCallback(() => {
    setAssignments(Object.fromEntries(step2Rows.map((row) => [row.id, ''])));
    setError(null);
    setStep(ASSIGN_STEP);
  }, [step2Rows]);

  const goBack = useCallback(() => {
    setStep(SELECT_STEP);
    setError(null);
  }, []);

  const handleSave = useCallback(() => {
    // The row's shown unit, so the payload matches the suffix beside the typed number.
    const entries = step2Rows
      .map((row) => ({
        rowId: row.id,
        quantity: assignments[row.id],
        unitSymbol: rowUnitSymbol(row, resolveRowUnitType(row, selections)),
      }))
      .filter((entry) => Number(entry.quantity) > 0);

    if (entries.length === 0) {
      setError(t('LINK_ASSIGN_REQUIRED'));
      return;
    }
    setError(null);
    onSave?.(entries);
  }, [step2Rows, selections, assignments, onSave, t]);

  const steps = useMemo(
    () => [{ label: t('ALLOCATIONS_LINK_STEP_SELECT') }, { label: t('LINK_STEP_ASSIGN') }],
    [t],
  );

  const detailRows = useMemo(() => buildAllocationDetailFields(t, allocation), [t, allocation]);

  const step1Columns = useMemo(
    () =>
      buildAllocationLinkStep1Columns(t, {
        headerSelect: unitHeaderSelect,
        onOpenDiscrepancy: handleOpenDiscrepancy,
      }),
    [t, unitHeaderSelect, handleOpenDiscrepancy],
  );

  const step2Columns = useMemo(
    () =>
      buildAllocationLinkStep2Columns(t, {
        headerSelect: unitHeaderSelect,
        assignments,
        onAssign: handleAssign,
        onRemove: handleRemove,
      }),
    [t, unitHeaderSelect, assignments, handleAssign, handleRemove],
  );

  const discrepancyAlerts = useMemo(
    () => buildSustainabilityMismatches(t, discrepancyRow),
    [t, discrepancyRow],
  );
  const discrepancySections = useMemo(
    () =>
      buildLinkShipmentDiscrepancySections(
        t,
        discrepancyRow,
        discrepancyRow && resolveRowUnitType(discrepancyRow, selections),
      ),
    [t, discrepancyRow, selections],
  );

  const emptyState = useMemo(() => ({ title: t('ALLOCATIONS_LINK_EMPTY') }), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const buttons = useMemo(() => {
    if (step === SELECT_STEP) {
      return [
        { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
        {
          label: t('COMMON_NEXT'),
          variant: ButtonVariants.primary,
          disabled: selectedIds.length === 0,
          onClick: goNext,
        },
      ];
    }
    return [
      { label: t('COMMON_PREVIOUS'), variant: ButtonVariants.ghost, onClick: goBack },
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        disabled: isSaving,
        loading: isSaving,
        onClick: handleSave,
      },
    ];
  }, [t, step, selectedIds.length, handleClose, goNext, goBack, handleSave, isSaving]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.large}
      title={t('ALLOCATIONS_LINK_TITLE', { transactionId: allocation?.transactionId ?? '' })}
      description={t('ALLOCATIONS_LINK_DESCRIPTION')}
      buttons={buttons}
    >
      <div className={styles.body}>
        <Stepper steps={steps} activeStep={step} variant="flush" />

        <SummaryCard
          heading={t('ALLOCATIONS_SECTION_ALLOCATION_DETAILS')}
          fields={detailRows}
          columns={3}
        />

        {error ? <Alert variant={AlertVariants.danger}>{error}</Alert> : null}

        {!loading && step === SELECT_STEP && shipments.length === 0 ? (
          <Alert variant={AlertVariants.warning}>{t('ALLOCATIONS_LINK_NONE_BODY')}</Alert>
        ) : null}

        <UnitTypeContext.Provider value={contextValue}>
          {step === SELECT_STEP ? (
            <Table
              key={`select-${session}`}
              columns={step1Columns}
              data={shipments}
              loading={loading}
              getRowId={getRowId}
              searchable
              onSearchChange={handleSearchChange}
              selectable
              onSelectionChange={handleSelectionChange}
              emptyState={emptyState}
              labels={tableLabels}
            />
          ) : (
            <Table
              key={`assign-${session}`}
              columns={step2Columns}
              data={step2Rows}
              getRowId={getRowId}
              emptyState={emptyState}
              labels={tableLabels}
            />
          )}
        </UnitTypeContext.Provider>
      </div>

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.shipmentId}
        discrepancies={discrepancyAlerts}
        sections={discrepancySections}
      />
    </Modal>
  );
};
